import { Component, DestroyRef, ElementRef, inject, Input, OnDestroy, OnInit, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { Subject, takeUntil } from 'rxjs';
import { MatSnackBar } from '@angular/material/snack-bar';
import { IProduct } from 'src/app/models/products/product';
import { IAsset } from 'src/app/models/products/asset';
import { ITouch } from 'src/app/models/products/touch';
import { AuthService } from 'src/app/services/auth.service';
import { Permission } from 'src/app/constants/permission.constant';
import { ProductsService } from 'src/app/services/products.service';
import { MasterSuitesService } from 'src/app/services/mastersuites.service';
import { SignalrService } from 'src/app/services/signalr.service';
import { SignalrGroup } from 'src/app/services/signalr-group';
import { SignalRIdGroups } from 'src/app/constants/signalr.constant';
import { EnhancementVariantDetail } from 'src/app/models/mastersuites/mastersuite';
import {
  parseAsset,
  parseStepContentData,
  setAllSelected,
  selectAllInTouch,
  onCardSelected,
  checkAllSelected,
  countSelectedAssets,
  isDownloadDisabled,
  hasSelectableAssets,
  ParseAssetContext,
} from '../product-asset.utils';

@Component({
    selector: 'app-step-production',
    templateUrl: './step-production.component.html',
    styleUrls: ['./step-production.component.scss'],
    changeDetection: ChangeDetectionStrategy.Default,
    standalone: false
})
export class StepProductionComponent implements OnInit, OnDestroy {
  @Input() product!: IProduct;
  @Input() colPerScreen = 3;
  @Input() editCallback!: (data: any) => void;
  @ViewChild('cardItem') cardItemEl!: ElementRef;

  Permission = Permission;
  content: any[] = [];
  listProduction: any[] = [];
  isLoading = false;
  allSelected = false;
  selectedCount = 0;
  displaySelectTouch: { [key: string]: boolean } = {};
  enhancementVariantIdSelected: string | null = '';
  enhancementVariants!: EnhancementVariantDetail[];
  rowHeight = 'fit';

  private readonly destroyRef = inject(DestroyRef);
  private destroy$ = new Subject();

  constructor(
    public authService: AuthService,
    private productsService: ProductsService,
    private masterSuiteService: MasterSuitesService,
    private signalrService: SignalrService,
    private _alert: MatSnackBar,
  ) {}

  ngOnInit(): void {
    this.isLoading = true;
    this.getProductionData();
    this.getEnhancementVariant();
    this.joinAndSubscribeToSignalR();
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.complete();
    this.signalrService.clearShootAssets();
  }

  getProductionData(): void {
    if (!this.product.id) return;

    this.listProduction = [];
    this.productsService
      .getListProduction(this.product.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (value: ITouch[]) => {
          this.listProduction = [];
          value.forEach((touch) => {
            const assetData: any[] = [];
            touch.assets.forEach((asset) => {
              assetData.push({ ...asset, selected: false });
              if (!!asset.copyAssets && asset.copyAssets.length > 0) {
                asset.copyAssets.forEach((copyAsset, index) => {
                  assetData.push({ ...asset, ...copyAsset, childIndex: index + 1, selected: false });
                });
              }
            });
            this.listProduction.push({
              ...touch,
              assets: assetData,
              selectedTouch: false,
              quantitySelected: 0,
            });
          });
          this.content = parseStepContentData(this.listProduction, this.parseContext);
          this.listProduction.forEach((p: any) => {
            this.displaySelectTouch[p.id] = hasSelectableAssets(this.content, p.id);
          });
          this.allSelected = false;
          this.selectedCount = 0;
          this.isLoading = false;
        },
      });
  }

  private get parseContext(): ParseAssetContext {
    return {
      step: 'production',
      listProduction: this.listProduction,
      hasPreReleasePermission: this.authService.hasPermission(Permission.PostAssetsPreRelease),
    };
  }

  getEnhancementVariant(): void {
    if (!this.product.masterSuiteId) return;
    this.masterSuiteService
      .getEnhancementVariantOfMasterSuite({ MasterSuiteId: this.product.masterSuiteId })
      .subscribe({
        next: (res) => {
          this.enhancementVariants = res.results;
          this.enhancementVariantIdSelected = this.product.masterSuiteEnhancementVariantId;
        },
      });
  }

  private joinAndSubscribeToSignalR(): void {
    if (!this.authService.hasPermission(Permission.PostAssetsPreRelease)) return;
    SignalrGroup.joinWithId({
      signalrService: this.signalrService,
      destroyRef: this.destroyRef,
      group: SignalRIdGroups.ProductShootAssets,
      id: this.product.id!,
      data$: this.signalrService.shootAssetsData,
      onData: (data: IAsset) => {
        const touchIndex = this.content.findIndex((x: any) => x.id === data.masterSuiteTouchId);
        if (touchIndex < 0) return;

        const assetData: any[] = [{ ...data }];
        if (data.copyAssets != null) {
          data.copyAssets.forEach((copyAsset, index) => {
            assetData.push({ ...data, ...copyAsset, childIndex: index + 1 });
          });
        }
        const assets = parseAsset(assetData, this.parseContext);
        assets.forEach((asset: any) => {
          const index = this.content[touchIndex].assets.findIndex(
            (a: any) => a.masterSuiteTouchAssetId === asset.masterSuiteTouchAssetId,
          );
          if (index >= 0) {
            if (this.content[touchIndex].assets[index].id === asset.id) {
              this.content[touchIndex].assets[index].status =
                this.authService.hasPermission(Permission.PostAssetsPreRelease)
                  ? asset.internalReviewState
                  : asset.clientReviewState;
            } else {
              this.content[touchIndex].assets[index] = asset;
            }
          }
        });
      },
    });
  }

  setAll(completed: boolean): void {
    const result = setAllSelected(this.content, completed);
    this.allSelected = completed;
    this.selectedCount = result.count;
  }

  selectedAllTouch(completed: boolean, touch: any): void {
    selectAllInTouch(this.content, touch.id, completed);
    this.allSelected = checkAllSelected(this.content);
    this.selectedCount = countSelectedAssets(this.content);
  }

  selectedCard(card: { touchId: string; data: any }): void {
    onCardSelected(this.content, card.touchId);
    this.allSelected = checkAllSelected(this.content);
    this.selectedCount = countSelectedAssets(this.content);
  }

  disableButtonDownload(): boolean {
    return isDownloadDisabled(this.content);
  }

  downloadMultiple(): void {
    let count = 0;
    this.content.forEach((t: any) => {
      t.assets.forEach((asset: any) => {
        if (asset.selected) {
          setTimeout(
            () => {
              const tmp = asset.rawUri.split('?')[0].split('/');
              const fileName = tmp[tmp.length - 1].split('.');
              const download = `${fileName[0]}.${fileName[1]}`;
              this.productsService
                .downloadSingleImage(asset.rawUri.split('//')[1])
                .subscribe((res) => {
                  const blob: Blob = res.body as Blob;
                  const a = document.createElement('a');
                  const fileData = window.URL.createObjectURL(blob);
                  a.href = fileData;
                  a.download = download;
                  a.click();
                  window.URL.revokeObjectURL(fileData);
                });
            },
            (count += 3000),
          );
        }
      });
    });
  }

  downloadImageToText(): void {
    this.productsService
      .downloadImageToText(this.product.id!, 'shoot-asset')
      .subscribe((blob) => {
        const a = document.createElement('a');
        const url = window.URL.createObjectURL(blob);
        a.href = url;
        a.download = `${this.product.barcode}_shoot-asset-content.csv`;
        a.click();
        window.URL.revokeObjectURL(url);
      });
  }

  onEnhancementVariantChanged(enhancementVariantId: string | null): void {
    this.enhancementVariantIdSelected = enhancementVariantId;
    this.product.masterSuiteEnhancementVariantId = this.enhancementVariantIdSelected;
    this.isLoading = true;
    this.getProductionData();
  }

  transferProductAsset(touchAssetId: string): void {
    const body: { shootAssetIds?: string[] } = {};
    this.content.forEach((t: any) => {
      if (t.id === touchAssetId) {
        t.assets.forEach((asset: any) => {
          if (asset.selected) {
            body.shootAssetIds
              ? body.shootAssetIds.push(asset.id)
              : (body.shootAssetIds = [asset.id]);
          }
        });
      }
    });
    this.productsService.transferAssetPlaceholder(body, 'shootAssets').subscribe(() => {
      this._alert.open('Transfer successfully!', 'close', {
        horizontalPosition: 'center',
        verticalPosition: 'bottom',
        duration: 3000,
        panelClass: ['success'],
      });
    });
  }

  updateRowHeight(): void {
    setTimeout(() => {
      if (!this.cardItemEl) return;
      this.rowHeight = `${this.cardItemEl.nativeElement.firstChild.offsetWidth}:${this.cardItemEl.nativeElement.firstChild.offsetHeight}`;
      this.isLoading = false;
    });
  }
}
